"use client"

import { useState, useEffect } from "react"
import { Play, X } from "lucide-react"

export function VideoModalPlayer({ youtubeId, title, description, className = "" }) {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isHovered, setIsHovered] = useState(false)

    // Handle modal
    const openModal = () => {
        setIsModalOpen(true)
        document.body.style.overflow = "hidden"
    }

    const closeModal = () => {
        setIsModalOpen(false)
        document.body.style.overflow = "unset"
    }

    // Close modal on escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === "Escape") closeModal()
        }

        if (isModalOpen) {
            document.addEventListener("keydown", handleEscape)
            return () => document.removeEventListener("keydown", handleEscape)
        }
    }, [isModalOpen])

    const thumbnailUrl = `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`

    return (
        <>
            {/* Main Video Component */}
            <div
                className={`relative group cursor-pointer overflow-hidden rounded-2xl bg-black shadow-2xl transition-all duration-500 hover:scale-[1.02] hover:shadow-3xl ${className}`}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={openModal}
            >
                {/* YouTube Thumbnail Background */}
                <div
                    className="w-full h-full bg-cover bg-center transition-all duration-700 group-hover:scale-105"
                    style={{ backgroundImage: `url(${thumbnailUrl})` }}
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent transition-all duration-500 group-hover:from-black/60" />

                {/* Play Button */}
                <div
                    className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ${isHovered ? "opacity-90 scale-110" : "opacity-100 scale-100"}`}
                >
                    <div className="bg-red-600/90 backdrop-blur-sm rounded-full p-6 transition-all duration-300 group-hover:bg-red-500 group-hover:scale-110 shadow-2xl">
                        <Play className="w-12 h-12 text-white ml-1" fill="currentColor" />
                    </div>
                </div>

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform transition-all duration-500 group-hover:translate-y-0 translate-y-2">
                    <h3 className="text-2xl font-bold mb-2 transition-all duration-300 group-hover:text-red-300">{title}</h3>
                    <p className="text-white/80 text-sm leading-relaxed transition-all duration-300 group-hover:text-white">
                        {description}
                    </p>
                </div>

                {/* YouTube Logo Indicator */}
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <div className="bg-red-600/90 backdrop-blur-sm rounded px-3 py-1 text-white text-xs font-bold">YouTube</div>
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/90 backdrop-blur-sm animate-in fade-in duration-300"
                        onClick={closeModal}
                    />

                    {/* Modal Content */}
                    <div className="relative z-10 w-full max-w-5xl mx-4 animate-in zoom-in-95 fade-in duration-500">
                        {/* Close Button */}
                        <button
                            onClick={closeModal}
                            className="absolute -top-12 right-0 text-white/80 hover:text-white transition-colors duration-200 z-20"
                        >
                            <X className="w-8 h-8" />
                        </button>

                        {/* YouTube Video Container */}
                        <div className="relative bg-black rounded-2xl overflow-hidden shadow-2xl aspect-video">
                            <iframe
                                className="w-full h-[500px] md:h-[650px] lg:h-[700px]"
                                src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                                title={title}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                            {/* Video Info Overlay */}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 pointer-events-none">
                                <h2 className="text-white text-2xl font-bold mb-2">{title}</h2>
                                <p className="text-white/80 text-sm">{description}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
